/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ModalFooter } from 'src/customText/verticals/projectCreationModal/components/ModalFooter';
import {
    ProjectBasicInfo,
    projectCreationContentLeftMargin,
    ProjectCreationSteps,
    ProjectInfoDto
} from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import styled from 'styled-components';

export type ProjectCreationModalBasicInfoProps = {
    onSubmit: (selectedProjectType: ProjectBasicInfo) => void;
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: ProjectInfoDto;
    isExtractionProject: boolean;
};

const messages = defineMessages({
    title: {
        id: 'customText.basicInfoScreen.title',
        defaultMessage: 'Enter basic information'
    },
    description: {
        id: 'customText.basicInfoScreen.description',
        defaultMessage: `Enter the basic information for your custom text extraction model
such as name and description and select the language which your documents are in.`
    },
    azureResourceLabel: {
        id: 'customText.basicInfoScreen.azureResourceLabel',
        defaultMessage: 'Azure resource'
    },
    azureResourcePlaceholder: {
        id: 'customText.basicInfoScreen.azureResourcePlaceholder',
        defaultMessage: 'No item selected'
    },
    changeAzureResource: {
        id: 'customText.basicInfoScreen.changeAzureResource',
        defaultMessage: 'Change Azure resource'
    },
    nameLabel: {
        id: 'customText.basicInfoScreen.nameLabel',
        defaultMessage: 'Name'
    },
    namePlaceholder: {
        id: 'customText.basicInfoScreen.namePlaceholder',
        defaultMessage: 'Type project name'
    },
    descriptionLabel: {
        id: 'customText.basicInfoScreen.descriptionLabel',
        defaultMessage: 'Description'
    },
    descriptionPlaceholder: {
        id: 'customText.basicInfoScreen.descriptionPlaceholder',
        defaultMessage: 'Type project description'
    },
    languageLabel: {
        id: 'customText.basicInfoScreen.languageLabel',
        defaultMessage: 'Text primary language'
    },
    languageTooltipText: {
        id: 'customText.basicInfoScreen.languageTooltipText',
        defaultMessage:
            'This is the language of your text documents. If you have multilingual dataset, select the language of the majority of your documents. This will used as the default language for your documents.'
    },
    languageLabelPlaceholder: {
        id: 'customText.basicInfoScreen.languageLabelPlaceholder',
        defaultMessage: 'Select the language of the text'
    },
    multiLingualLabel: {
        id: 'customText.basicInfoScreen.multiLingualLabel',
        defaultMessage: 'Does your dataset include documents that are not in the same language?'
    },
    multiLingualText: {
        id: 'customText.basicInfoScreen.multiLingualText',
        defaultMessage: 'Yes, enable multi-lingual dataset'
    },
    multiLingualTooltipText: {
        id: 'customText.basicInfoScreen.multiLingualTooltipText',
        defaultMessage:
            'Enable this option if your dataset has text documents that are not in the same language. Several languages might work well when grouped together,'
    },
    invalidProjName: {
        id: 'customText.basicInfoScreen.invalidProjName',
        defaultMessage: 'Invalid characters, you can only use "-", "_", "." and alphanumeric characters.'
    },
    duplicatedNameErrorMessage: {
        id: 'customText.basicInfoScreen.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    learnMore: {
        id: 'customText.basicInfoScreen.learnMore',
        defaultMessage: 'Learn more about multi-lingual datasets.'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationModalBasicInfo');

const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });
const stepTheme = getScopedTheme('ProjectCreationModalBasicInfo');
const StyledDropdown = styled(LuisDropdown)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '300px' : '100%'
}));
const StyledTextField = styled(LuisTextField)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '300px' : '100%'
}));

export const BasicInfoScreen = observer((props: ProjectCreationModalBasicInfoProps) => {
    const { onSubmit, onDismiss, onPrevious, projectDto, isExtractionProject } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { csAccountStore } = rootStore.azureStore.session;
    const { customTextCultureStorehouse, customTextProjectStoreHouse: customTextProjectsStoreHouse } = rootStore;
    const { isLargeOrGreater } = useResponsiveMode();

    const { formState, formStateDto, updateFormState } = useFormState<ProjectBasicInfo>({
        multiLingual: [projectDto?.multiLingual],
        projectDescription: [projectDto?.projectDescription],
        defaultLanguage: [projectDto?.defaultLanguage, FormValidators.createRequiredValidator()],
        projectName: [
            projectDto?.projectName,
            FormValidators.createRequiredValidator(),
            FormValidators.createLengthValidator(LuisConstants.CustomText.maxNamesCharLength),
            FormValidators.createBlacklistValidator({
                mode: 'include',
                errorMessage: messages.invalidProjName,
                characterList: LuisConstants.ResourceLimits.AllowedNameReges
            })
        ]
    });

    const duplicatedProjName = customTextProjectsStoreHouse.items.find(p => p.name === formState.fields.projectName.value);
    const authoringResourcesOptions: IDropdownOption[] = (csAccountStore && [{ key: csAccountStore.id, text: csAccountStore.name }]) || [];
    const languagesOptions: IDropdownOption[] = customTextCultureStorehouse.items.map(c => ({ key: c.code, text: c.name }));

    const isAuthoringResourceSelected = !!csAccountStore;

    const isModalValid = Boolean(isAuthoringResourceSelected && formState.form.valid && !duplicatedProjName);

    React.useEffect(() => {
        dispatcher.dispatch('getCustomTextCultures', { toastData: {} });
    }, []);

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode, text: string, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {label}
            </LabelContainer>
            <CalloutButton isDarkMode={false} ariaLabel={text} content={tooltipText} />
        </Stack>
    );

    return (
        <Stack>
            <Stack
                styles={{ root: { maxHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 ${projectCreationContentLeftMargin}` : '12px 20px'}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>
                        {localize(messages.description)}&nbsp;
                        <ExternalLink
                            styles={{ root: { color: stepTheme.calloutLinkColor } }}
                            href={`https://aka.ms/${isExtractionProject ? 'ct-multilingual' : 'ctc-multilingual'}`}>
                            {localize(messages.learnMore)}
                        </ExternalLink>
                    </LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack.Item>
                        <StyledDropdown
                            disabled
                            isLargeOrGreater={isLargeOrGreater}
                            options={authoringResourcesOptions}
                            selectedKey={csAccountStore?.id}
                            label={localize(messages.azureResourceLabel)}
                            placeholder={localize(messages.azureResourcePlaceholder)}
                        />
                        <LuisText variant="small">
                            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                                {localize(messages.changeAzureResource)}
                            </Link>
                        </LuisText>
                    </Stack.Item>

                    <StyledTextField
                        required
                        maxLength={50}
                        isLargeOrGreater={isLargeOrGreater}
                        label={localize(messages.nameLabel)}
                        disabled={!isAuthoringResourceSelected}
                        value={formState.fields.projectName.value}
                        placeholder={localize(messages.namePlaceholder)}
                        onChange={(_, val) => updateFormState({ projectName: val })}
                        errorMessage={
                            duplicatedProjName ? localize(messages.duplicatedNameErrorMessage) : formState.fields.projectName.errorMessage
                        }
                    />

                    <StyledDropdown
                        options={languagesOptions}
                        isLargeOrGreater={isLargeOrGreater}
                        ariaLabel={localize(messages.languageLabel)}
                        selectedKey={formState.fields.defaultLanguage.value}
                        isLoading={customTextCultureStorehouse.tracker.isRunning}
                        onChange={(_, o) => updateFormState({ defaultLanguage: o.key as string })}
                        placeholder={localize(messages.languageLabelPlaceholder)}
                        onRenderLabel={() =>
                            labelOnRender(
                                localize(messages.languageLabel),
                                <LuisText styles={{ root: { outline: 'none !important' } }} tabIndex={0}>
                                    {localize(messages.languageTooltipText)}
                                </LuisText>,
                                localize(messages.languageTooltipText)
                            )
                        }
                    />

                    <Stack>
                        {labelOnRender(
                            localize(messages.multiLingualLabel),
                            localize(messages.multiLingualTooltipText),
                            `${localize(messages.multiLingualTooltipText)} ${localize(globalMessages.learnMore)}`,
                            false
                        )}

                        <Checkbox
                            label={localize(messages.multiLingualText)}
                            checked={formState.fields.multiLingual.value}
                            onChange={() => updateFormState({ multiLingual: !formState.fields.multiLingual.value })}
                        />
                    </Stack>

                    <StyledTextField
                        isLargeOrGreater={isLargeOrGreater}
                        placeholder={localize(messages.descriptionPlaceholder)}
                        disabled={!isAuthoringResourceSelected}
                        value={formState.fields.projectDescription.value}
                        onChange={(_, val) => updateFormState({ projectDescription: val })}
                        label={localize(messages.descriptionLabel)}
                    />
                </Stack>
            </Stack>

            <ModalFooter
                isValid={isModalValid}
                onDismiss={onDismiss}
                onNext={() => onSubmit({ ...formStateDto })}
                onPrevious={onPrevious}
                isLoading={customTextCultureStorehouse.tracker.isRunning}
                activeStep={ProjectCreationSteps.EnterBasicInformation}
            />
        </Stack>
    );
});
